import {Page} from 'Core/models/Page';
import React, {CSSProperties, ReactNode, useContext} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import {FixedSizeList as List} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import {Column} from 'Shared/components/PagedView/PagedView';
import {ColumnWidthsContext, ROW_SIZE} from 'Shared/components/PagedView/Table/Table';
import LoadingRow from 'Shared/components/PagedView/Table/TableLoadingRow';
import 'Shared/components/Table/Table.sass';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';

type TableBodyProps<TData> = {
  data: Page<TData>;
  loadMore: (page: Page<TData>) => void;
  setHeight: (height: number) => void;
  setWidth: (width: number) => void;
  columns: Array<Column<TData>>;
};

const getItemCountWithSpaceForLoading = (totalCount: number, currentResultCount: number) =>
  totalCount === currentResultCount ? currentResultCount : currentResultCount + 1;

const TableBody = <TData extends {}>(props: TableBodyProps<TData>) => {
  const {columns, loadMore, data, setHeight, setWidth} = props;

  return (
    <AutoSizer
      onResize={({height, width}) => {
        setHeight(height);
        setWidth(width);
      }}
    >
      {({height, width}) => (
        <InfiniteLoader
          isItemLoaded={index => Boolean(data.result[index])}
          threshold={3}
          itemCount={data.totalCount + 1}
          loadMoreItems={(start, stop) => {
            if (stop > data.result.length && start < data.totalCount) {
              loadMore(data);
            }

            return null;
          }}
        >
          {({onItemsRendered, ref}) => (
            <List
              onItemsRendered={onItemsRendered}
              ref={ref}
              height={height}
              width={width}
              overscanCount={5}
              itemCount={getItemCountWithSpaceForLoading(data.totalCount, data.result.length)}
              itemSize={ROW_SIZE}
            >
              {({index, style}) => (
                <TableRow key={`table-row__${index}`} columns={columns} data={data} index={index} style={style} />
              )}
            </List>
          )}
        </InfiniteLoader>
      )}
    </AutoSizer>
  );
};

type TableRowProps<TData> = {
  style: CSSProperties;
  columns: Array<Column<TData>>;
  index: number;
  data: Page<TData>;
};

const TableRow = <TData extends {}>(props: TableRowProps<TData>) => {
  const {style, columns, index, data} = props;
  const isLoadingRow = (index: number) => index === data.result.length;

  return isLoadingRow(index) ? (
    <LoadingRow style={style} />
  ) : (
    <div style={style} className="paged-table__row">
      {columns.map(col => (
        <TableCell
          id={`table-row-${toKebabCase(col.title)}-${index}`}
          title={col.title}
          key={`cell_${toKebabCase(col.title)}_${index}`}
          value={col.getContent(data.result[index])}
        />
      ))}
    </div>
  );
};

const TableCell = (props: {id: string; title: string; value: ReactNode}) => {
  const {id, title, value} = props;
  const columnWidths = useContext(ColumnWidthsContext);
  const width = columnWidths.get(title);

  return (
    <div id={id} style={{width: `${width}%`}} className="paged-table__cell">
      {value}
    </div>
  );
};

export default TableBody;
